import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Empty,
    Input,
    Modal,
    Popconfirm,
    Select,
    Slider,
    Space,
    Table,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { axiosInstance } from '../config';
import { fetchModels } from '../components/BananasGPT/api/gptClient';
import {
    getReasoningEfforts,
    REASONING_EFFORT_LABELS,
    resolveReasoningEffort,
} from '../components/BananasGPT/utils/reasoning';
import ContextMessagesEditor, {
    blankContextRow,
    fromContextDraftRows,
    toContextDraftRows,
} from '../components/reviews/ContextMessagesEditor';
import { DEFAULT_PREVIEW_PROMPT } from '../components/reviews/reviewPrompt';

const emptyDraft = () => ({
    id: null,
    name: '',
    model: null,
    temperature: 0.7,
    reasoning_effort: null,
    preview_prompt: DEFAULT_PREVIEW_PROMPT,
    main_prompt: '',
    rows: [blankContextRow()],
});

const snapTemperature = (raw) => {
    const number = Number(raw);
    if (!Number.isFinite(number)) return 0.7;
    return Math.round(Math.min(2, Math.max(0, number)) * 10) / 10;
};

const ReviewReplyTemplates = () => {
    const [templates, setTemplates] = useState([]);
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [draft, setDraft] = useState(emptyDraft);

    const selectedModel = useMemo(
        () => models.find((item) => item.id === draft.model) || null,
        [models, draft.model]
    );
    const reasoningEfforts = useMemo(
        () => getReasoningEfforts(selectedModel?.reasoning),
        [selectedModel]
    );

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/v1/reviews/reply-templates');
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось загрузить шаблоны ответов'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    useEffect(() => {
        let cancelled = false;
        fetchModels('openrouter')
            .then((data) => {
                if (!cancelled) setModels(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!cancelled) {
                    message.warning(
                        'Не удалось загрузить модели BananasGPT. Проверьте ключ OpenRouter в профиле.'
                    );
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const openCreate = () => {
        setDraft(emptyDraft());
        setEditorOpen(true);
    };

    const openEdit = (template) => {
        setDraft({
            id: template.id,
            name: template.name || '',
            model: template.model || null,
            temperature: snapTemperature(template.temperature),
            reasoning_effort: template.reasoning_effort || null,
            preview_prompt: template.preview_prompt || DEFAULT_PREVIEW_PROMPT,
            main_prompt: template.main_prompt || '',
            rows: toContextDraftRows(template.context_messages),
        });
        setEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorOpen(false);
        setDraft(emptyDraft());
    };

    const modelOptions = useMemo(() => {
        const options = models.map((item) => ({
            value: item.id,
            label: item.name || item.id,
        }));
        if (draft.model && !options.some((item) => item.value === draft.model)) {
            options.unshift({ value: draft.model, label: draft.model });
        }
        return options;
    }, [models, draft.model]);

    const changeModel = (modelId) => {
        const nextModel = models.find((item) => item.id === modelId) || null;
        const efforts = getReasoningEfforts(nextModel?.reasoning);
        setDraft((current) => ({
            ...current,
            model: modelId || null,
            reasoning_effort: efforts.length
                ? resolveReasoningEffort(nextModel?.reasoning, current.reasoning_effort)
                : null,
        }));
    };

    const saveDraft = async () => {
        const name = draft.name.trim();
        if (!name) {
            message.info('Укажите название шаблона');
            return;
        }
        const payload = {
            name,
            model: draft.model || null,
            temperature: snapTemperature(draft.temperature),
            reasoning_effort: reasoningEfforts.length
                ? draft.reasoning_effort || resolveReasoningEffort(
                      selectedModel?.reasoning,
                      draft.reasoning_effort
                  )
                : null,
            context_messages: fromContextDraftRows(draft.rows),
            preview_prompt: draft.preview_prompt || DEFAULT_PREVIEW_PROMPT,
            main_prompt: draft.main_prompt || '',
            clear_model: !draft.model,
            clear_reasoning: !reasoningEfforts.length,
        };
        setSaving(true);
        try {
            if (draft.id) {
                await axiosInstance.patch(
                    `/v1/reviews/reply-templates/${encodeURIComponent(draft.id)}`,
                    payload
                );
                message.success('Шаблон сохранён');
            } else {
                await axiosInstance.post('/v1/reviews/reply-templates', {
                    name: payload.name,
                    model: payload.model,
                    temperature: payload.temperature,
                    reasoning_effort: payload.reasoning_effort,
                    preview_prompt: payload.preview_prompt,
                    main_prompt: payload.main_prompt,
                    context_messages: payload.context_messages,
                });
                message.success('Шаблон создан');
            }
            closeEditor();
            await loadTemplates();
        } catch (error) {
            message.error(error.response?.data?.detail || 'Не удалось сохранить шаблон');
        } finally {
            setSaving(false);
        }
    };

    const removeTemplate = async (template) => {
        try {
            await axiosInstance.delete(
                `/v1/reviews/reply-templates/${encodeURIComponent(template.id)}`
            );
            message.success('Шаблон удалён');
            await loadTemplates();
        } catch (error) {
            message.error(error.response?.data?.detail || 'Не удалось удалить шаблон');
        }
    };

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            render: (value) => <Typography.Text strong>{value || 'Без названия'}</Typography.Text>,
        },
        {
            title: 'Модель',
            dataIndex: 'model',
            key: 'model',
            render: (value) => {
                const found = models.find((item) => item.id === value);
                return found?.name || value || '—';
            },
        },
        {
            title: 'Temperature',
            dataIndex: 'temperature',
            key: 'temperature',
            width: 130,
            render: (value) => snapTemperature(value).toFixed(1),
        },
        {
            title: 'Размышление',
            dataIndex: 'reasoning_effort',
            key: 'reasoning_effort',
            width: 140,
            render: (value) => REASONING_EFFORT_LABELS[value] || value || '—',
        },
        {
            title: 'Контекст',
            dataIndex: 'context_messages',
            key: 'context_messages',
            width: 110,
            render: (value) => `${Array.isArray(value) ? value.length : 0} сообщ.`,
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 170,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(record)}
                    >
                        Изменить
                    </Button>
                    <Popconfirm
                        title="Удалить шаблон?"
                        okText="Удалить"
                        cancelText="Отмена"
                        onConfirm={() => removeTemplate(record)}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="container mx-auto">
            <Card>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <Typography.Title level={2} style={{ margin: 0 }}>
                            Шаблоны ответов
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Контекст, модель и параметры BananasGPT для ответов на отзывы
                        </Typography.Text>
                    </div>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Создать шаблон
                    </Button>
                </div>

                <div className="mt-6">
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={templates}
                        loading={loading}
                        size="small"
                        pagination={false}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Шаблонов пока нет"
                                />
                            ),
                        }}
                    />
                </div>
            </Card>

            <Modal
                title={draft.id ? 'Изменить шаблон' : 'Новый шаблон'}
                open={editorOpen}
                onCancel={closeEditor}
                width={760}
                destroyOnClose
                styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
                footer={[
                    <Button key="cancel" onClick={closeEditor}>
                        Отмена
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={saving}
                        onClick={saveDraft}
                    >
                        Сохранить
                    </Button>,
                ]}
            >
                <div className="space-y-4">
                    <div>
                        <div className="mb-1 text-sm font-medium">Название</div>
                        <Input
                            value={draft.name}
                            maxLength={120}
                            placeholder="Например: Вежливый ответ на 5 звёзд"
                            onChange={(event) =>
                                setDraft((current) => ({
                                    ...current,
                                    name: event.target.value,
                                }))
                            }
                        />
                    </div>

                    <div>
                        <div className="mb-1 text-sm font-medium">Модель нейросети</div>
                        <Select
                            className="w-full"
                            showSearch
                            allowClear
                            placeholder="Выберите модель BananasGPT"
                            optionFilterProp="label"
                            value={draft.model || undefined}
                            options={modelOptions}
                            onChange={changeModel}
                        />
                    </div>

                    <div>
                        <div className="mb-1 text-sm font-medium">
                            Temperature: {snapTemperature(draft.temperature).toFixed(1)}
                        </div>
                        <Slider
                            min={0}
                            max={2}
                            step={0.1}
                            value={snapTemperature(draft.temperature)}
                            onChange={(value) =>
                                setDraft((current) => ({
                                    ...current,
                                    temperature: snapTemperature(value),
                                }))
                            }
                        />
                    </div>

                    {reasoningEfforts.length ? (
                        <div>
                            <div className="mb-1 text-sm font-medium">
                                Уровень размышления
                            </div>
                            <Select
                                className="w-full"
                                value={
                                    resolveReasoningEffort(
                                        selectedModel?.reasoning,
                                        draft.reasoning_effort
                                    ) || undefined
                                }
                                options={reasoningEfforts.map((effort) => ({
                                    value: effort,
                                    label: REASONING_EFFORT_LABELS[effort] || effort,
                                }))}
                                onChange={(value) =>
                                    setDraft((current) => ({
                                        ...current,
                                        reasoning_effort: value,
                                    }))
                                }
                            />
                        </div>
                    ) : null}

                    <div>
                        <div className="mb-1 text-sm font-medium">Контекст до ответа</div>
                        <Typography.Paragraph type="secondary" className="!mb-3">
                            Эти сообщения добавляются в начало запроса к модели перед текстом
                            отзыва. Роль: system / user / assistant / tool или своя.
                        </Typography.Paragraph>
                        <ContextMessagesEditor
                            value={draft.rows}
                            onChange={(rows) =>
                                setDraft((current) => ({ ...current, rows }))
                            }
                        />
                    </div>

                    <div>
                        <div className="mb-1 text-sm font-medium">Предварительный промт</div>
                        <Typography.Paragraph type="secondary" className="!mb-3">
                            Подставляется перед ответом модели. Плейсхолдеры в фигурных скобках
                            заменяются данными конкретного отзыва:{' '}
                            <code>{'{offer_name}'}</code>, <code>{'{offer_id}'}</code>,{' '}
                            <code>{'{photos_count}'}</code>, <code>{'{videos_count}'}</code>,{' '}
                            <code>{'{photos_array}'}</code>, <code>{'{rating}'}</code>,{' '}
                            <code>{'{positive}'}</code>, <code>{'{negative}'}</code>,{' '}
                            <code>{'{comment}'}</code>.
                        </Typography.Paragraph>
                        <Input.TextArea
                            value={draft.preview_prompt}
                            rows={11}
                            onChange={(event) =>
                                setDraft((current) => ({
                                    ...current,
                                    preview_prompt: event.target.value,
                                }))
                            }
                        />
                    </div>

                    <div>
                        <div className="mb-1 text-sm font-medium">Основной промт</div>
                        <Typography.Paragraph type="secondary" className="!mb-3">
                            Опишите, что нужно ответить и в каком стиле. При генерации
                            предварительный и основной промт объединяются в один запрос к модели.
                        </Typography.Paragraph>
                        <Input.TextArea
                            value={draft.main_prompt}
                            rows={8}
                            placeholder="Например: Поблагодари покупателя коротко и по-дружески, без канцелярита."
                            onChange={(event) =>
                                setDraft((current) => ({
                                    ...current,
                                    main_prompt: event.target.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ReviewReplyTemplates;
